
























import { Component, Prop, Vue } from 'vue-property-decorator';
import Cart from '@/components/payment/Cart.vue';
import RecordLocationSelect from '@/components/payment/RecordLocationSelect.vue';
import { ProductGroupTypes } from '@/enums/ProductGroupTypes';
import { Action } from 'vuex-class';
import { OrderSummary } from '@/interfaces/OrderSummary';
import { ProjectData } from '@/interfaces/ProjectData';
import { ProjectListItem } from '@/interfaces/ProjectListItem';
import { eventBus, eventBusEvents } from '@/helpers/eventBusHelper';

interface CheckoutData {
    recordLocation: {
        type: null | string;
        name: null | string;
        description?: string;
    };
}

interface Fields {
    name: string | null;

    [key: string]: string | null;
}

@Component({
    name: 'ActiveFunds',
    components: {
        Cart,
        RecordLocationSelect,
    },
})
export default class ActiveFunds extends Vue {
    protected get myProjectsList() {
        return this.$store.getters['projects/myProjectsList'];
    }

    protected get shouldVATNotificationShow() {
        return this.checkoutMethod === 'usePayPerView';
    }

    protected get cartData() {
        if (!this.orderSummary) {
            return [];
        }
        return [
            {
                name: 'Remaining record credits',
                value: this.orderSummary.remainingCredits,
            },
            {
                name: 'Number of chargeable records',
                value: '-' + this.orderSummary.numberOfChargeableRecords,
            },
            {
                name: 'Total records remaining',
                value: this.orderSummary.totalRecordRemaining,
            },
        ];
    }

    @Action('user/buyUsingCredits') public buyUsingCredits!: (
        id: number,
    ) => Promise<unknown>;
    @Action('user/buyUsingSubscriptions') public buyUsingSubscriptions!: (
        id: number,
    ) => Promise<unknown>;
    @Prop() protected orderSummary!: OrderSummary;
    @Prop() protected checkoutMethod!: string;
    @Action('user/getShoppingCartRecords')
    protected getShoppingCartRecords!: () => void;

    protected errorMessages: Fields = {
        name: null,
    };

    protected folders = [];
    protected isDisabled = false;

    protected checkoutData: CheckoutData = {
        recordLocation: {
            type: null,
            name: null,
        },
    };

    @Action('projects/getExistingProjectsWithGroup')
    private getExistingProjectsWithGroup!: (
        productGroup: ProductGroupTypes,
    ) => Promise<unknown>;
    @Action('projects/addProject')
    private addProject!: (projectData: ProjectData) => void;

    protected async mounted() {
        eventBus.$emit(eventBusEvents.activateGlobalLoading);
        await this.getExistingProjectsWithGroup(
            this.$store.getters['user/activeProductGroup'],
        ).then(() => {
            eventBus.$emit(eventBusEvents.deactivateGlobalLoading);
        });
        this.folders = this.$store.getters['projects/myProjectsList'];
    }

    protected onNewFolderChange(newFolderData: {
        name: string;
        description: string;
        type: string;
    }) {
        this.checkoutData.recordLocation = newFolderData;
    }

    protected onExistingFolderChange(newFolderData: {
        name: string;
        type: string;
    }) {
        this.checkoutData.recordLocation = newFolderData;
    }

    protected async onSubmit() {
        if (!this.checkoutData.recordLocation.name) {
            this.$message.error('Please select a destination folder!');
            return;
        }
        this.isDisabled = true;
        if (this.checkoutData.recordLocation.type !== 'existingProject') {
            try {
                await this.addProject({
                    name: this.checkoutData.recordLocation.name,
                    description: this.checkoutData.recordLocation.description
                        ? this.checkoutData.recordLocation.description
                        : '',
                });
            } catch (e) {
                this.isDisabled = false;
                const pointerArray = e.response.data.errors[0].source.pointer.split(
                    '/',
                );
                const pointer = pointerArray[pointerArray.length - 1];
                this.errorMessages[pointer] = e.response.data.errors[0].detail;
                this.$message.error(e.response.data.errors[0].detail);
                return new Error(e);
            }
        }
        const project = this.myProjectsList.find(
            (projectItem: ProjectListItem) =>
                projectItem.name === this.checkoutData.recordLocation.name,
        );
        if (!project) {
            throw new Error('Project not found');
        }
        if (this.checkoutMethod === 'usePrepaid') {
            await this.buyUsingCredits(project.id).catch((e) => {
                if (e.response.data) {
                    this.$message.error(e.response.data.errors[0].detail);
                    this.isDisabled = false;
                }
                throw new Error('Not enough funds');
            });
        } else {
            await this.buyUsingSubscriptions(project.id).catch((e) => {
                if (e.response.data) {
                    this.$message.error(e.response.data.errors[0].detail);
                    this.isDisabled = false;
                }
                throw new Error('Not enough funds');
            });
        }
        await this.getShoppingCartRecords();
        this.$router.push({ name: 'MyProjects' });
    }

    protected get buttonText() {
        return this.checkoutMethod === 'usePrepaid'
            ? 'Checkout now against your credit'
            : 'Checkout now';
    }
}
